import React from "react";
import { Playlist } from "./playlist/Playlist";
import { Block, BlockType, ILiveStream, IMedia, IMediaRating, IMemberPlan, IPlaylistResponseDto, IUploadCategory, PlaylistTemplate } from "../../types";
import { blocksService, configService, liveChannelService, mediaService } from "../../services";
import { WebAnalyticsContext } from "../../context";
import * as Styled from "./BlockCollection.styled";
import { BlockCollectionModalNonFullscreenStyle } from "./BlockCollection.styled";
import { CembooPlayer } from "@haydenfilms-institute/cemboo-player";
import ReactPlayer from "react-player";
import { templateComponentMap } from "../../libs/utils/templateComponentMap";
import { CembooThemeProvider } from "../../libs/themes";
import { Layout1 } from "../layouts";
import { Layout1 as MobileVodLayout } from "../layouts-mobile/Layout1";
import { EventTabs } from "../event-tabs";
import { useIsMobile, useScrollLock } from "../hooks";
import * as Dialog from "@radix-ui/react-dialog";
import { XLg } from "react-bootstrap-icons";

export const BlockCollection: React.FC<{
    clientId: string;
    outletId: string;
    fetchByDate: string;
    enableRatingControls: boolean;
    sessionToken: string;
    isSignedIn: boolean;
    mediaPerPage: number;
    enableCompetitionMode?: boolean;
    enableAutoplay: boolean;
    disableFullscreenPlayer: boolean;
    previewMode: boolean;
    profileId?: string;
    versionId?: string;
    themeMode?: "light" | "dark";
}> = ({
    clientId,
    outletId,
    fetchByDate,
    enableRatingControls,
    sessionToken,
    isSignedIn,
    mediaPerPage,
    enableCompetitionMode,
    enableAutoplay,
    disableFullscreenPlayer,
    previewMode,
    profileId: previewProfileId,
    versionId: previewVersionId,
    themeMode = "light"
}) => {
        const isMobile = useIsMobile();
        const [showLoader, setShowLoader] = React.useState(true);
        const [blocks, setBlocks] = React.useState<Block[]>([]);
        const [currentPlayingMediaItem, setCurrentPlayingMediaItem] =
            React.useState<IMedia | ILiveStream | undefined>(undefined);
        const [hasFullAccess, setHasFullAccess] = React.useState(false);
        const [showPlayerModal, setShowPlayerModal] = React.useState(false);
        useScrollLock(showPlayerModal);
        const [trialDetailsFetched, setTrialDetailsFetched] = React.useState(false);
        // const [showUpdateToast, setShowUpdateToast] = React.useState(false);
        const [activePlans, setActivePlans] = React.useState<IMemberPlan[]>([]);
        const [media, setMedia] = React.useState<IMedia[]>([]);

        // TODO: Genre should be taken from the global context which is in
        // sync with MediaCollection component

        const [mediaRating, setMediaRating] = React.useState<IMediaRating[]>([]);
        const [useCembooPlayer, setUseCembooPlayer] = React.useState(true);
        const [freeTrialPlanIds, setFreeTrialPlanIds] = React.useState<string[]>(
            []
        );
        // const [isPlaying, setIsPlaying] = React.useState(false);
        const [generalVodTemplateId, setGeneralVodTemplateId] = React.useState<number>(0);
        const [filmFestivalVodTemplateId, setFilmFestivalVodTemplateId] = React.useState<number>(0);

        const [playerAnalyticsData, setPlayerAnalyticsData] =
            React.useState<any>(null);
        const { updateVideoWebAnalyticsData, setSessionToken } =
            React.useContext(WebAnalyticsContext);

        const [livestream, setLivestream] = React.useState<ILiveStream[]>([]);

        const fetchOutletAndProfile = React.useCallback(async () => {
            try {
                setShowLoader(true);

                let fetchedProfileId = "";
                let fetchedVersionId = undefined;

                if (previewMode) {
                    fetchedProfileId = previewProfileId || "";
                    fetchedVersionId = previewVersionId;

                    if (!fetchedProfileId) {
                        console.error("Preview mode enabled but profileId is missing");
                        setShowLoader(false);
                        return;
                    }
                } else {
                    const outletInfo = await blocksService.getLibraryOutlet(outletId, clientId, previewMode);
                    fetchedProfileId = outletInfo?.profileId;

                    if (!fetchedProfileId) {
                        console.error("No profile ID found for outlet");
                        setShowLoader(false);
                        return;
                    }
                }

                const populatedProfileInfo = await blocksService.getPopulatedLibraryOutlet(
                    outletId,
                    clientId,
                    fetchedProfileId,
                    fetchedVersionId,
                    previewMode
                );

                console.log("Populated Profile Info: ", populatedProfileInfo);

                const blocksOrder = populatedProfileInfo?.version?.blocksOrder || [];
                const fetchedBlocks = populatedProfileInfo?.blocks || [];

                const orderedBlocks = blocksOrder
                    .map(blockId => fetchedBlocks.find(block => block.blockId === blockId))
                    .filter((block): block is Block => block !== undefined && block.enabled);

                const blocksWithDefaults = orderedBlocks.map(block => ({
                    ...block,
                    renderInfo: {
                        ...block.renderInfo,
                        templateId: block.renderInfo?.templateId ?? 0,
                        mobileTemplateId: block.renderInfo?.mobileTemplateId ?? 0,
                    }
                }));

                setBlocks(blocksWithDefaults);

            } catch (err) {
                console.error("Error fetching outlet and profile:", err);
            } finally {
                setShowLoader(false);
            }
        }, [outletId, clientId, previewMode, previewProfileId, previewVersionId]);

        const handlePreviewCall = React.useCallback(() => {

        }, []);

        React.useEffect(() => {
            window.addEventListener("updatePreviewConfig", handlePreviewCall);

            return () => {
                window.removeEventListener("updatePreviewConfig", handlePreviewCall);
            };
        }, [handlePreviewCall]);

        React.useEffect(() => {
            fetchOutletAndProfile();
        }, [fetchOutletAndProfile]);

        const fetchLivestreams = React.useCallback(async () => {
            try {
                const livestreams = await liveChannelService.getAllLiveEvents(clientId);
                setLivestream(livestreams.liveStreams);
            } catch (err) {
                console.log("Error fetching livestreams: ", err);
            }
        }, [clientId]);

        React.useEffect(() => {
            fetchLivestreams();
        }, [fetchLivestreams]);

        const getAllMediaRatings = React.useCallback(async () => {
            setSessionToken(sessionToken);
            try {
                const ratings = await mediaService.getAllMediaRatings(sessionToken);
                setMediaRating(ratings);
            } catch (err) {
                console.log(err);
            }
        }, [sessionToken, setSessionToken]);

        const getAllMedia = React.useCallback(async () => {
            try {
                const [result, vodTemplates] = await Promise.all([
                    mediaService.getAllMedia(clientId, fetchByDate, previewMode),
                    configService.getLibraryTemplates(clientId, previewMode),
                ]);
                setMedia(result);

                const generalVodTemplate = vodTemplates.templateIds.find(t => t.blockType === BlockType.GENERAL_VOD);
                const filmFestivalVodTemplate = vodTemplates.templateIds.find(t => t.blockType === BlockType.FILM_FESTIVAL_VOD);
                setGeneralVodTemplateId(generalVodTemplate?.value ? parseInt(generalVodTemplate.value) : 0);
                setFilmFestivalVodTemplateId(filmFestivalVodTemplate?.value ? parseInt(filmFestivalVodTemplate.value) : 0);
            } catch (err) {
                console.log(err);
            }
        }, [
            clientId,
            fetchByDate,
            previewMode,
            setMedia,
        ]);

        const hasVodBlocks = React.useMemo(
            () => blocks.some(b => b.type === BlockType.GENERAL_VOD || b.type === BlockType.FILM_FESTIVAL_VOD),
            [blocks]
        );

        React.useEffect(() => {
            if (hasVodBlocks) {
                getAllMedia();
            }
        }, [getAllMedia, hasVodBlocks]);


        React.useEffect(() => {
            if (enableRatingControls && isSignedIn) {
                getAllMediaRatings();
            }
        }, [isSignedIn, enableRatingControls, getAllMediaRatings]);

        const fetchCustomerActivePlans = React.useCallback(async () => {
            const result = await mediaService.getCustomerPurchasedPlans(
                sessionToken!
            );

            if (!trialDetailsFetched) {
                try {
                    const trailData = await mediaService.checkTrialCodeUser(
                        sessionToken!
                    );
                    if (trailData.length > 0) {
                        setHasFullAccess(trailData[0].isFullAccess);
                        setFreeTrialPlanIds(trailData[0].planId);
                    }
                    setTrialDetailsFetched(true);
                } catch (err) {
                    console.log(err);
                }
            }

            setActivePlans(
                result.filter((item) =>
                    item.expiryDate
                        ? new Date(item.expiryDate).getTime() > Date.now()
                        : true
                )
            );
        }, [sessionToken, trialDetailsFetched]);

        React.useEffect(() => {
            window.addEventListener("onFetchUserPlansEvent", () => {
                if (sessionToken) {
                    fetchCustomerActivePlans();
                }
            });
        }, [fetchCustomerActivePlans, sessionToken]);

        React.useEffect(() => {
            if (isSignedIn && sessionToken) {
                fetchCustomerActivePlans();
                setSessionToken(sessionToken);
            }
        }, [fetchCustomerActivePlans, setSessionToken, isSignedIn, sessionToken]);

        const generateLoadSignInModalEvent = React.useCallback(
            (event: IMedia, showPurchasePlanModal?: boolean) => {
                const displayLoginEvent = new CustomEvent("loadSignInModal", {
                    detail: {
                        eventRequested: event,
                        showPurchasePlanModal: showPurchasePlanModal,
                        requestedPlanType: 1,
                    },
                });
                window.dispatchEvent(displayLoginEvent);

            },
            []
        );

        const handleWatchNowClick = React.useCallback(
            async (item: IMedia) => {
                console.log("[BlockCollection] handleWatchNowClick called. item:", item);
                console.log("[BlockCollection] isSignedIn:", isSignedIn, "activePlans:", activePlans, "hasFullAccess:", hasFullAccess);
                console.log("[BlockCollection] item.planDetails:", item.planDetails);
                if (isSignedIn) {
                    const hasAccess = !!item.planDetails.length
                        ? !!activePlans.filter((memberPlan) =>
                            item.planDetails?.some((itemPlan) => {
                                return itemPlan?.id === memberPlan.id;
                            })
                        ).length ||
                        freeTrialPlanIds.includes(item.planDetails[0].id) ||
                        hasFullAccess
                        : true;
                    console.log("[BlockCollection] hasAccess:", hasAccess);
                    if (hasAccess) {
                        try {
                            console.log("[BlockCollection] Opening player modal. Setting useCembooPlayer to true (reset), showPlayerModal=true");
                            setUseCembooPlayer(true);
                            setShowPlayerModal(true);
                            setCurrentPlayingMediaItem(item);
                        } catch (err) {
                            console.log("Error while fetching token is: ", err);
                        }
                    } else {
                        console.log("[BlockCollection] No access — showing sign in modal.");
                        generateLoadSignInModalEvent(item);
                    }
                } else {
                    if (!!item.planDetails.length) {
                        console.log("[BlockCollection] Not signed in, item has plan — showing sign in modal.");
                        generateLoadSignInModalEvent(item);
                    } else {
                        console.log("[BlockCollection] Not signed in, free item — opening player. Setting useCembooPlayer to true (reset)");
                        setUseCembooPlayer(true);
                        setShowPlayerModal(true);
                        setCurrentPlayingMediaItem(item);
                    }
                }
            },
            [activePlans, isSignedIn, freeTrialPlanIds, hasFullAccess, generateLoadSignInModalEvent]
        );

        const handleRatingSuccess = () => {
            getAllMediaRatings();
        };

        const handleSwitchPlayer = React.useCallback(() => {
            setUseCembooPlayer(prev => {
                const next = !prev;
                console.warn(`[BlockCollection] handleSwitchPlayer called. useCembooPlayer: ${prev} -> ${next}`);
                console.trace("[BlockCollection] switchPlayer call stack:");
                return next;
            });
        }, []);

        const handlePlayerAnalytics = React.useCallback((data: any) => {
            console.log("Analytics data from player: ", data);
            setPlayerAnalyticsData(data);
        }, []);

        const insertMediaActivity = React.useCallback(() => {
            const anonId = localStorage.getItem("anonId");
            const mediaId = currentPlayingMediaItem && 'mediaId' in currentPlayingMediaItem ? currentPlayingMediaItem.mediaId : undefined;
            if (playerAnalyticsData && !showPlayerModal) {
                if (playerAnalyticsData.watchTime > 5) {
                    updateVideoWebAnalyticsData({
                        anonId: anonId || "",
                        mediaId: mediaId || "",
                        rewindsSkips:
                            playerAnalyticsData.rewinds + playerAnalyticsData.skips,
                        engagement: playerAnalyticsData.engagement,
                        watchTime: playerAnalyticsData.watchTime,
                        isCompleted: playerAnalyticsData.isCompleted,
                    });
                }

                setPlayerAnalyticsData(null);
            }
        }, [
            updateVideoWebAnalyticsData,
            showPlayerModal,
            currentPlayingMediaItem,
            playerAnalyticsData,
        ]);

        React.useEffect(() => {
            window.addEventListener("beforeunload", () => {
                setShowPlayerModal(false);
                insertMediaActivity();
            });

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        React.useEffect(() => {
            insertMediaActivity();
        }, [showPlayerModal, insertMediaActivity]);

        if (showLoader) {
            return (
                <Styled.LoaderWrapper>
                    <Styled.Spinner />
                </Styled.LoaderWrapper>
            );
        }

        const VodTemplateComponent = isMobile ? MobileVodLayout : (templateComponentMap[generalVodTemplateId as PlaylistTemplate] || Layout1);
        const FilmFestivalVodTemplateComponent = isMobile ? MobileVodLayout : (templateComponentMap[filmFestivalVodTemplateId as PlaylistTemplate] || Layout1);

        const renderBlock = (block: Block) => {
            const templateId = block.renderInfo?.templateId ?? 0;
            const LayoutComponent = isMobile ? MobileVodLayout : templateComponentMap[templateId as PlaylistTemplate];

            switch (block.type) {
                case BlockType.GENERAL_VOD:
                    const GeneralVodComponent = LayoutComponent || VodTemplateComponent;
                    return (
                        <GeneralVodComponent
                            key={block.blockId}
                            playlistTitle="VOD Collection"
                            mediaItems={media.filter(item => item.category === IUploadCategory.GENERAL)}
                            mediaRating={mediaRating}
                            onWatchClick={handleWatchNowClick}
                            enableRatingControls={enableRatingControls}
                            isSignedIn={isSignedIn}
                            hasFullAccess={hasFullAccess}
                            activePlans={activePlans}
                            freeTrialPlanIds={freeTrialPlanIds}
                            sessionToken={sessionToken}
                            enableCompetitionMode={enableCompetitionMode}
                            onRatingSuccess={handleRatingSuccess}
                            mediaPerPage={mediaPerPage}
                        />
                    );

                case BlockType.FILM_FESTIVAL_VOD:
                    const FilmFestivalComponent = LayoutComponent || FilmFestivalVodTemplateComponent;
                    return (
                        <FilmFestivalComponent
                            key={block.blockId}
                            playlistTitle="Film Festival VOD Collection"
                            mediaItems={media.filter(item => item.category === IUploadCategory.FILM_FESTIVAL)}
                            mediaRating={mediaRating}
                            onWatchClick={handleWatchNowClick}
                            enableRatingControls={enableRatingControls}
                            isSignedIn={isSignedIn}
                            hasFullAccess={hasFullAccess}
                            activePlans={activePlans}
                            freeTrialPlanIds={freeTrialPlanIds}
                            sessionToken={sessionToken}
                            enableCompetitionMode={enableCompetitionMode}
                            onRatingSuccess={handleRatingSuccess}
                            mediaPerPage={mediaPerPage}
                        />
                    );

                case BlockType.PLAYLIST:
                    // Use the populated payload data from the block
                    const playlistData = block.payload as IPlaylistResponseDto;

                    if (!playlistData) {
                        console.log(`No payload found for playlist block with ID: ${block.blockId}, ${block.payload}`);
                        return null;
                    }

                    return LayoutComponent ? (
                        <LayoutComponent
                            key={block.blockId}
                            mediaItems={playlistData.mediaItems || []}
                            playlistTitle={playlistData.title || ""}
                            mediaRating={mediaRating}
                            onWatchClick={(i) => handleWatchNowClick(i)}
                            onRatingSuccess={() => handleRatingSuccess()}
                            isSignedIn={isSignedIn}
                            sessionToken={sessionToken}
                            activePlans={activePlans}
                            hasFullAccess={hasFullAccess}
                            freeTrialPlanIds={freeTrialPlanIds}
                            enableCompetitionMode={enableCompetitionMode}
                            enableRatingControls={enableRatingControls}
                            refreshMediaRatings={getAllMediaRatings}
                        />
                    ) : (
                        <Playlist
                            key={block.blockId}
                            playlist={playlistData}
                            clientId={clientId}
                            enableRatingControls={enableRatingControls}
                            sessionToken={sessionToken}
                            isSignedIn={isSignedIn}
                            mediaPerPage={mediaPerPage}
                            templateId={templateId}
                            enableCompetitionMode={enableCompetitionMode}
                            enableAutoplay={enableAutoplay}
                        />
                    );

                case BlockType.LIVE_STREAMS:
                    return (
                        <EventTabs
                            key={block.blockId}
                            clientId={clientId}
                            sessionToken={sessionToken}
                            isSignedIn={isSignedIn}
                            enableShareControls={true}
                            enableTabControls={true}
                            templateId={block?.renderInfo?.templateId ?? 0}
                            externalLiveEvents={livestream}
                            setCurrentlyStreamingEvent={(value: ILiveStream | undefined) => { setCurrentPlayingMediaItem(value); console.log(value?.streamingUrl) }}
                            setShowPlayerModal={(value: boolean) => setShowPlayerModal(value)}
                        />

                    );
                default:
                    return null;
            }
        };


        return (
            <CembooThemeProvider mode={themeMode}>
            <>
                <div className="position-relative" data-cemboo="block-collection">
                    {blocks.map(block => renderBlock(block))}

                    {disableFullscreenPlayer && <BlockCollectionModalNonFullscreenStyle />}

                    <Dialog.Root
                        open={showPlayerModal}
                        onOpenChange={(open: boolean) => !open && setShowPlayerModal(false)}
                        modal={false}
                    >
                        <Dialog.Portal>
                            <Styled.DialogOverlay data-cemboo="block-collection-modal-overlay" />
                            <Styled.DialogContent
                                data-cemboo="block-collection-modal-content"
                                $nonFullscreen={disableFullscreenPlayer}
                                className={"cemboo-video-modal" + (disableFullscreenPlayer ? " cemboo-video-modal-nonfullscreen" : "")}
                            >
                                <Styled.ModalHeader data-cemboo="block-collection-modal-header">
                                    <Styled.ModalHeaderRow>
                                        <Styled.ModalTitle data-cemboo="block-collection-modal-title">
                                            {currentPlayingMediaItem?.title}
                                        </Styled.ModalTitle>
                                        <Styled.ModalHeaderActions>
                                            <Styled.HeaderButton onClick={handleSwitchPlayer}>
                                                Switch Player
                                            </Styled.HeaderButton>
                                            <Styled.CloseButton data-cemboo="block-collection-modal-close" aria-label="Close">
                                                <XLg size={20} />
                                            </Styled.CloseButton>
                                        </Styled.ModalHeaderActions>
                                    </Styled.ModalHeaderRow>
                                </Styled.ModalHeader>
                                <Styled.ModalBody data-cemboo="block-collection-modal-body">
                                    {(() => {
                                        const resolvedUrl = currentPlayingMediaItem
                                            ? ('video' in currentPlayingMediaItem ? currentPlayingMediaItem.video : currentPlayingMediaItem.streamingUrl || "")
                                            : "";
                                        console.log("[BlockCollection] Rendering modal body. useCembooPlayer:", useCembooPlayer);
                                        console.log("[BlockCollection] currentPlayingMediaItem:", currentPlayingMediaItem);
                                        console.log("[BlockCollection] resolvedUrl:", resolvedUrl);
                                        console.log("[BlockCollection] 'video' in item?", currentPlayingMediaItem ? 'video' in currentPlayingMediaItem : "no item");
                                        if (currentPlayingMediaItem && 'video' in currentPlayingMediaItem) {
                                            console.log("[BlockCollection] item.video:", (currentPlayingMediaItem as any).video);
                                        }
                                        if (currentPlayingMediaItem && 'streamingUrl' in currentPlayingMediaItem) {
                                            console.log("[BlockCollection] item.streamingUrl:", (currentPlayingMediaItem as any).streamingUrl);
                                        }
                                        return null;
                                    })()}
                                    {useCembooPlayer ? (
                                        <CembooPlayer
                                            url={
                                                currentPlayingMediaItem
                                                    ? ('video' in currentPlayingMediaItem ? currentPlayingMediaItem.video : currentPlayingMediaItem.streamingUrl || "")
                                                    : ""
                                            }
                                            showThumbnail={true}
                                            // switchPlayer={handleSwitchPlayer}
                                            onVideoAnalyticsUpdate={handlePlayerAnalytics}
                                        />
                                    ) : (
                                        <ReactPlayer
                                            url={currentPlayingMediaItem && ('video' in currentPlayingMediaItem ? currentPlayingMediaItem.video : currentPlayingMediaItem.streamingUrl)}
                                            width={"100%"}
                                            height={"100%"}
                                            controls
                                        />
                                    )}
                                </Styled.ModalBody>
                            </Styled.DialogContent>
                        </Dialog.Portal>
                    </Dialog.Root>
                </div>
            </>
            </CembooThemeProvider>
        );
    };
